import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Layout } from "providers/Layout";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CreatableSelect,
  DateInput,
  Input
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Loader } from "shared/components/Loader";
import { Subscriber } from "views/rent-reporting-id/types";

import { getErrorMessage } from "utils/error";
import { UnsubscribeModal } from "./components/UnsubscribeModal";
import { subscriberValidations } from "./validations";
import { demaskPhoneValue } from "shared/components/forms/Input/helpers/phone-mask";
import { Redirect } from "shared/components/Redirect";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { Switch } from "shared/components/forms/Switch";
import addressApi from "api/landlord/address";
import { Property } from "types/landlord/address";
import { getPropertyNameWithLongAddress } from "shared/addressUtils";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { states } from "utils/stateCodes";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import { useFormTypedWithPartial } from "shared/app/LeadsTableView/hooks/useFormTypedWithPartial";

export const EditSubscriptionDetails = () => {
  const { type, permissions } = useAuthContext();

  const { values, handleChange, register, reset, onSubmit, errors } =
    useFormTypedWithPartial(
      {
        lease_end_date: "",
        lease_month_to_month: false,
        monthly_rent_amount: 0,
        moved_out: false,
        move_out_date: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        dob: "",
        property_id: 0,
        unit_id: 0,
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: 0,
        move_in_date: "",
        ssn: ""
      },
      subscriberValidations
    );
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [property, setProperty] = useState<Property | undefined>();
  const [unit, setUnit] = useState<{ id: number; title: string } | undefined>();
  const [unitCacheUniq, setUnitCacheUniq] = useState(0);
  const [isSsnEditing, setIsSsnEditing] = useState(false);
  const [originalSsnMasked, setOriginalSsnMasked] = useState("");
  const [leaseEndDateKey, setLeaseEndDateKey] = useState(0);
  const [isLoadingPropertyAddress, setIsLoadingPropertyAddress] =
    useState(false);

  const valuesRef = useRef(values);
  valuesRef.current = values;

  const { Unit, Property } = useUnitsPropertiesDictionary();

  const { renterId } = useParams();
  const navigate = useNavigate();
  const { data: renter, isFetching } = useQuery({
    queryKey: ["SUBSCRIBER_DETAIL", renterId],
    queryFn: () =>
      instance
        .get(`/portal/v1/customers/${renterId}`)
        .then(({ data: renter }: { data: Subscriber }) => {
          let maskedSsn = "";
          if (renter.ssn) {
            if (renter.ssn.length === 4)
              // Only last 4 digits from API
              maskedSsn = `***-**-${renter.ssn}`;
            else if (renter.ssn.length >= 9)
              // Full SSN from API (unlikely), show only last 4
              maskedSsn = `***-**-${renter.ssn.slice(-4)}`;
            else maskedSsn = renter.ssn;

            setOriginalSsnMasked(maskedSsn);
          }

          reset({
            first_name: renter.first_name || "",
            last_name: renter.last_name || "",
            phone: demaskPhoneValue(renter.phone) || "",
            dob: renter.dob || "",
            email: renter.email || "",
            lease_end_date: renter.lease_end_date || "",
            lease_month_to_month: renter.lease_month_to_month || false,
            monthly_rent_amount:
              (renter.current_month_payment?.cents || 0) / 100 || 0,
            moved_out: renter.moved_out || Boolean(renter.move_out_date),
            move_out_date: renter.move_out_date || "",
            property_id: renter.property?.id || 0,
            unit_id: renter.unit_id || 0,
            move_in_date: renter.move_in_date || "",
            ssn: maskedSsn,
            address1: "",
            address2: "",
            city: "",
            state: "",
            zip: 0
          });

          if (renter.unit_id && renter.unit)
            setUnit({
              id: renter.unit_id,
              title: renter.unit
            });

          return renter;
        })
  });

  // Fetch full property details when property_id exists but property state is not set
  useEffect(() => {
    if (values.property_id && !property && renter) {
      setIsLoadingPropertyAddress(true);
      addressApi
        .getProperty(String(values.property_id))
        .then((propertyData: Property) => {
          setProperty(propertyData);

          reset({
            ...valuesRef.current,
            address1: propertyData.address.address1 || "",
            address2: propertyData.address.address2 || "",
            city: propertyData.address.city || "",
            state: propertyData.address.state_or_province || "",
            zip: propertyData.address.postal_code
              ? Number(propertyData.address.postal_code)
              : 0
          });

          setIsLoadingPropertyAddress(false);
        })
        .catch(() => {
          setIsLoadingPropertyAddress(false);
        });
    }
  }, [values.property_id, property, renter]);

  const loadPropertyOptionsWithLongAddress = async (
    search: string,
    _,
    { page }
  ) => {
    const data = await addressApi.getProperties({
      searchTerm: search,
      page
    });

    return {
      options: data?.items?.map((item) => ({
        value: item.id,
        label: getPropertyNameWithLongAddress(item, item.address)
      })),
      hasMore: data.has_more,
      additional: {
        page: page + 1
      }
    };
  };

  const loadUnitOptionsForProperty = async (
    search: string,
    _,
    { page, propertyId }: { page: number; propertyId?: number }
  ) => {
    if (!propertyId) return { options: [], hasMore: false };

    const data = await addressApi.getUnitsOfProperty({
      propertyId,
      searchTerm: search || "",
      page
    });

    return {
      options: data?.items?.map((item) => ({
        value: item.id,
        label: item.title
      })),
      hasMore: data.has_more,
      additional: {
        page: page + 1,
        propertyId
      }
    };
  };

  const propertyChangeHandler = (e: any) => {
    const propertyId = e?.target?.value || e?.value || 0;
    const propertyIdNumber =
      typeof propertyId === "string" ? Number(propertyId) : propertyId;

    setUnitCacheUniq((uniq) => uniq + 1);
    setUnit(undefined);

    if (propertyIdNumber) {
      setIsLoadingPropertyAddress(true);
      addressApi
        .getProperty(String(propertyIdNumber))
        .then((propertyData: Property) => {
          setProperty(propertyData);

          reset({
            ...valuesRef.current,
            property_id: propertyIdNumber,
            unit_id: 0,
            address1: propertyData.address.address1 || "",
            address2: propertyData.address.address2 || "",
            city: propertyData.address.city || "",
            state: propertyData.address.state_or_province || "",
            zip: propertyData.address.postal_code
              ? Number(propertyData.address.postal_code)
              : 0
          });

          setIsLoadingPropertyAddress(false);
        })
        .catch(() => {
          setIsLoadingPropertyAddress(false);
        });
    } else {
      setProperty(undefined);
      reset({
        ...valuesRef.current,
        property_id: 0,
        unit_id: 0,
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: 0
      });
    }
  };

  const { mutate: updateDetails } = useMutation({
    mutationKey: ["SUBSCRIBERS", renterId],
    mutationFn: (data: {
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      dob: string;
      lease_end_date: string | null;
      lease_month_to_month: boolean;
      monthly_rent_amount: number;
      address1: string;
      address2: string;
      city: string;
      state: string;
      zip: number;
      move_out_date: string;
      property_id?: number;
      unit_id?: number;
      move_in_date?: string;
      ssn?: string;
    }) => {
      return instance.patch(`/portal/v1/customers/${renterId}`, {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: demaskPhoneValue(data.phone),
        dob: data.dob,
        lease_end_date: data.lease_end_date,
        lease_month_to_month: data.lease_month_to_month,
        monthly_rent_amount: data.monthly_rent_amount,
        address1: data.address1,
        address2: data.address2,
        city: data.city,
        state: data.state,
        zip: data.zip,
        moved_out_date: data.move_out_date,
        property_id: data.property_id || undefined,
        unit_id: data.unit_id || undefined,
        move_in_date: data.move_in_date,
        // Only send SSN if it was edited (not masked or empty)
        ssn:
          data.ssn && !data.ssn.includes("*") && data.ssn.length > 0
            ? data.ssn.replace(/\D/g, "")
            : undefined
      });
    },
    onSuccess: () => {
      toast.success("Successfully updated renter details");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate: handleUnsubscribe } = useMutation({
    mutationKey: ["UNSUBSCRIBE", renterId],
    mutationFn: () => {
      return instance.delete(`/portal/v1/customers/${renterId}`);
    },
    onSuccess: () => {
      toast.success("Successfully unsubscribed");
      setShowUnsubscribeModal(false);
      navigate("/rent-reporting");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  if (!permissions.canEditSubscriptionDetails) return <Redirect href="/" />;

  if (isFetching)
    return (
      <Layout
        headerClassName="py-0"
        header={
          <>
            <div className="border-b border-alpha-40 p-6">
              <BreadCrumbs
                crumbs={[
                  {
                    title: "Rent reporting",
                    href: "/rent-reporting"
                  },
                  {
                    title: renter?.name || "",
                    href: `/rent-reporting/${renterId}`
                  },
                  {
                    title: "Edit",
                    href: `/rent-reporting/${renterId}/edit`
                  }
                ]}
              />
            </div>
            <div className="flex items-center justify-between border-b border-alpha-100 p-6">
              <h1 className="text-xl font-bold text-content-1000">
                Edit Subscription Details
              </h1>
              <Button
                variant={ButtonVariant.TEXT}
                size={ButtonSize.BASE}
                disabled={!permissions.canUnsubscribeRenters}
              >
                Unsubscribe
              </Button>
            </div>
          </>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Link to={`/rent-reporting/${renterId}`}>
              <Button size={ButtonSize.BASE} variant={ButtonVariant.BASE}>
                Cancel
              </Button>
            </Link>
            <Button size={ButtonSize.BASE} variant={ButtonVariant.PRIMARY}>
              Save
            </Button>
          </div>
        }
      >
        <div className="flex justify-center">
          <Loader />
        </div>
      </Layout>
    );

  if (type.isSDK) return <Redirect href="/" />;

  return (
    <Layout
      headerClassName="py-0"
      header={
        <>
          <div className="border-b border-alpha-40 p-6">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Rent reporting",
                  href: "/rent-reporting"
                },
                {
                  title: renter?.name || "",
                  href: `/rent-reporting/${renterId}`
                },
                {
                  title: "Edit",
                  href: `/rent-reporting/${renterId}/edit`
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between border-b border-alpha-100 p-6">
            <h1 className="text-xl font-bold text-content-1000">
              Edit Subscription Details
            </h1>
            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.BASE}
              onClick={() => setShowUnsubscribeModal(true)}
              disabled={!permissions.canUnsubscribeRenters}
            >
              Unsubscribe
            </Button>
          </div>
        </>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Link to={`/rent-reporting/${renterId}`}>
            <Button size={ButtonSize.BASE} variant={ButtonVariant.BASE}>
              Cancel
            </Button>
          </Link>
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            type="submit"
            form="edit-subscription-details-form"
            disabled={type.isPMS}
          >
            Save
          </Button>
        </div>
      }
    >
      <form
        id="edit-subscription-details-form"
        onSubmit={onSubmit({
          onSuccess: (data) =>
            updateDetails({
              first_name: data.first_name,
              last_name: data.last_name,
              email: data.email,
              phone: data.phone,
              dob: data.dob,
              lease_end_date: data.lease_month_to_month
                ? null
                : data.lease_end_date,
              lease_month_to_month: data.lease_month_to_month,
              monthly_rent_amount: data.monthly_rent_amount,
              address1: data.address1,
              address2: data.address2,
              city: data.city,
              state: data.state,
              zip: data.zip,
              move_out_date: data.move_out_date,
              property_id: data.property_id,
              unit_id: data.unit_id,
              move_in_date: data.move_in_date,
              ssn: data.ssn
            }),
          onError: (e) => {
            toast.error(Object.values(e).join(", "));
          }
        })}
      >
        <h2 className="text-sm font-bold text-content-1000">
          Subscription Details
        </h2>
        <div className="mb-4 mt-2 border-b border-alpha-100" />
        <div className="max-w-[756px]">
          <div className="w-1/2 pr-2 mb-6">
            <Input
              id="monthly_rent_amount"
              label="Current monthly rent"
              readOnly={type.isPMS}
              hideOptional
              type="money"
              {...register("monthly_rent_amount")}
            />
          </div>
          <div className="mb-6 flex gap-4">
            <div className="w-full">
              <DateInput
                id="move_in_date"
                label="Move-in date"
                {...register("move_in_date")}
                readOnly={type.isPMS}
                hideOptional
                placeholder="MM/DD/YYYY"
              />
            </div>
            <div className="w-full">
              <DateInput
                key={`lease-end-date-${leaseEndDateKey}`}
                id="lease_end_date"
                label="Lease end date"
                value={values.lease_end_date}
                onChange={handleChange("lease_end_date")}
                readOnly={Boolean(values.lease_month_to_month) || type.isPMS}
                hideOptional
                error={errors.lease_end_date}
              />
              <Checkbox
                label="It's month to month / no end date"
                id="lease_month_to_month"
                checked={values.lease_month_to_month}
                disabled={type.isPMS}
                onChange={() => {
                  const newValue = !values.lease_month_to_month;

                  reset({
                    ...valuesRef.current,
                    lease_month_to_month: newValue,
                    lease_end_date: newValue
                      ? ""
                      : valuesRef.current.lease_end_date
                  });

                  // Force DateInput to remount with fresh state when enabling month-to-month
                  if (newValue) setLeaseEndDateKey((prev) => prev + 1);
                }}
              />
            </div>
          </div>
          <div className="mb-4">
            <Switch
              label="Add Move-Out Date"
              disabled={type.isPMS}
              checked={values.moved_out}
              onChange={() =>
                handleChange("moved_out", (v) => v)(!values.moved_out)
              }
            />
          </div>
          <div className="mb-10 grid grid-cols-2">
            <DateInput
              key="move-out-date"
              id="moved_out_date"
              disabled={type.isPMS}
              readOnly={Boolean(!values.moved_out)}
              hideOptional
              {...register("move_out_date")}
            />
          </div>
        </div>
        <h2 className="text-sm font-bold text-content-1000">Address Details</h2>
        <div className="mb-4 mt-2 border-b border-alpha-100" />
        <div className="max-w-[756px]">
          <div className="mb-2 flex gap-4">
            <div className="w-full">
              <AsyncSelect
                key={`property-select-${property?.id || "empty"}`}
                id="property"
                disabled={type.isPMS || isLoadingPropertyAddress}
                label={Property}
                value={values.property_id}
                onChange={propertyChangeHandler}
                menuPortalTarget={document.body}
                searchable={true}
                menuPosition="fixed"
                menuPlacement="auto"
                hideOptional
                // @ts-expect-error
                loadOptions={loadPropertyOptionsWithLongAddress}
                defaultValue={
                  property?.address && property.id
                    ? {
                        value: property.id,
                        label: getPropertyNameWithLongAddress(
                          property,
                          property.address
                        )
                      }
                    : undefined
                }
                options={
                  property?.address && property.id
                    ? [
                        {
                          value: property.id,
                          label: getPropertyNameWithLongAddress(
                            property,
                            property.address
                          )
                        }
                      ]
                    : []
                }
                additional={{
                  page: 1
                }}
              />
            </div>
            <div className="w-full">
              <AsyncSelect
                key={`unit-select-${property?.id}-${unitCacheUniq}`}
                id="unit"
                disabled={type.isPMS || !property?.id}
                label={Unit}
                value={values.unit_id}
                onChange={(e: any) => {
                  const unitId = e?.target?.value || e?.value || 0;
                  const unitIdNumber =
                    typeof unitId === "string" ? Number(unitId) : unitId;
                  const unitLabel = e?.label || "";

                  reset({
                    ...valuesRef.current,
                    unit_id: unitIdNumber
                  });

                  if (unitIdNumber)
                    setUnit({
                      id: unitIdNumber,
                      title: unitLabel
                    });
                  else setUnit(undefined);
                }}
                menuPortalTarget={document.body}
                searchable={true}
                menuPosition="fixed"
                menuPlacement="auto"
                hideOptional
                // @ts-expect-error
                loadOptions={loadUnitOptionsForProperty}
                defaultValue={
                  unit?.id && unit.title
                    ? {
                        value: unit.id,
                        label: unit.title
                      }
                    : undefined
                }
                options={
                  unit?.id && unit.title
                    ? [
                        {
                          value: unit.id,
                          label: unit.title
                        }
                      ]
                    : []
                }
                additional={{
                  page: 1,
                  propertyId: property?.id
                }}
              />
            </div>
          </div>
          <div className="mb-2 flex gap-4">
            <div className="w-full">
              <Input
                id="address1"
                label="Street address"
                readOnly={type.isPMS || isLoadingPropertyAddress}
                hideOptional
                type="text"
                {...register("address1")}
              />
            </div>
            <div className="w-full">
              <Input
                id="address2"
                label="Apartment, suite, etc."
                readOnly={type.isPMS || isLoadingPropertyAddress}
                type="text"
                {...register("address2")}
              />
            </div>
          </div>
          <div className="mb-10 flex gap-4">
            <div className="w-full">
              <Input
                id="city"
                label="City"
                readOnly={type.isPMS || isLoadingPropertyAddress}
                hideOptional
                type="text"
                {...register("city")}
              />
            </div>
            <div className="w-full">
              <CreatableSelect
                id="state"
                label="State"
                isDisabled={type.isPMS || isLoadingPropertyAddress}
                hideOptional
                searchable
                isValidNewOption={() => false}
                options={states}
                {...register("state")}
              />
            </div>
            <div className="w-full">
              <Input
                id="zip"
                label="Zip"
                readOnly={type.isPMS || isLoadingPropertyAddress}
                type="text"
                hideOptional
                {...register("zip")}
              />
            </div>
          </div>
        </div>
        <h2 className="text-sm font-bold text-content-1000">
          Personal Details
        </h2>
        <div className="mb-4 mt-2 border-b border-alpha-100" />
        <div className="max-w-[756px]">
          <div className="mb-2 flex gap-4">
            <div className="w-full">
              <Input
                id="first_name"
                label="Legal first name"
                readOnly={type.isPMS}
                hideOptional
                {...register("first_name")}
              />
            </div>
            <div className="w-full">
              <Input
                id="last_name"
                label="Legal last name"
                readOnly={type.isPMS}
                hideOptional
                {...register("last_name")}
              />
            </div>
          </div>
          <div className="mb-2 flex gap-4">
            <div className="w-full">
              <Input
                id="email"
                label="Email"
                readOnly={type.isPMS}
                hideOptional
                {...register("email")}
              />
            </div>
            <div className="w-full">
              <Input
                id="phone"
                label="Phone"
                readOnly={type.isPMS}
                hideOptional
                {...register("phone")}
                placeholder="(123) 000-000"
                type="tel"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-full">
              <DateInput
                id="dob"
                readOnly={type.isPMS}
                label="Date of birth"
                hideOptional
                {...register("dob")}
              />
            </div>
            <div className="w-full">
              <Input
                id="ssn"
                label="SSN"
                readOnly={type.isPMS}
                hideOptional
                onFocus={() => {
                  if (originalSsnMasked && !isSsnEditing) {
                    setIsSsnEditing(true);
                    handleChange("ssn", () => "")();
                  }
                }}
                {...register("ssn", undefined, "___-__-____")}
                placeholder="***-**-****"
                type="text"
              />
            </div>
          </div>
        </div>
      </form>
      <UnsubscribeModal
        opened={showUnsubscribeModal}
        onClose={() => setShowUnsubscribeModal(false)}
        handleUnsubscribe={handleUnsubscribe}
      />
    </Layout>
  );
};
